import {
  ICustomField,
  IAttributeItem,
  IAvailableAttributesResponse,
  IMondayBoard,
  IMondayColumn,
} from "../interfaces";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "";

function getToken() {
  return localStorage.getItem("thackd_jwt");
}

export function removeToken() {
  localStorage.removeItem("thackd_jwt");
}

function fetchData(url: string, headers: any | null) {
  if (!headers) {
    headers = {};
  }

  return fetch(url, { headers: headers }).then((response) => response.json());
}

function postData(url: string, data: any, headers: any) {
  if (!headers) {
    headers = {
      "Content-Type": "application/json",
    };
  } else {
    headers = {
      ...headers,
      "Content-Type": "application/json",
    };
  }

  return fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify(data),
  });
}

export function login(email: string, password: string) {
  email = email.toLowerCase();
  return postData(SERVER_DOMAIN + "/api/token/", { email, password }, null);
}

export function signUp(email: string, password: string, profileId: string) {
  return postData(
    SERVER_DOMAIN + "/api/create-user/",
    { email, password, pid: profileId },
    null,
  );
}

export function getUser() {
  const token = getToken();
  if (token) {
    return fetchData(SERVER_DOMAIN + "/api/get-user/", {
      Authorization: `Bearer ${token}`,
    });
  } else {
    return false;
  }
}

export function getProfile(profileId: string) {
  return fetchData(SERVER_DOMAIN + `/api/get-profile/${profileId}/`, null);
}

export async function checkUserExists(email: string) {
  const response = await postData(
    SERVER_DOMAIN + "/api/check-user-exists/",
    { email },
    null,
  );
  const data = await response.json();
  return { email: data.email, isLegacy: data.isLegacy };
}

export function postCheckout(
  itemKey: string,
  product: "ASANA" | "MONDAY" | "PROFANITY_FILTER" | "IMAGE_BLOCKER",
  volumeLimit: number,
  profileId: string | null,
) {
  if (profileId) {
    // This is for Asana
    return postData(
      SERVER_DOMAIN + "/api/create-checkout-session/",
      {
        itemKey,
        product,
        volumeLimit,
        profileId,
      },
      null,
    );
  } else {
    // This is for profanity filter
    const token = getToken();
    return postData(
      SERVER_DOMAIN + "/api/create-checkout-session/",
      {
        itemKey,
        product,
        volumeLimit,
      },
      { Authorization: `Bearer ${token}` },
    );
  }
}

export function getManageSubscriptionURL() {
  const token = getToken();
  return fetchData(SERVER_DOMAIN + "/api/create-portal-session/", {
    Authorization: `Bearer ${token}`,
  });
}

export function orderSuccess(sessionId: string, profileId: string | null) {
  if (profileId) {
    return postData(
      SERVER_DOMAIN + "/api/order-success/",
      {
        sessionId,
        profileId,
      },
      null,
    );
  } else {
    const token = getToken();
    return postData(
      SERVER_DOMAIN + "/api/order-success/",
      {
        sessionId,
      },
      { Authorization: `Bearer ${token}` },
    );
  }
}

export function resetPassword(email: string) {
  return postData(SERVER_DOMAIN + "/api/reset-password/", { email }, null);
}

export function getIntercomWorkspace(profileId: string) {
  return fetchData(
    SERVER_DOMAIN + `/api/get-intercom-workspace?profileId=${profileId}`,
    null,
  );
}

type CsShieldStatsType = {
  num_images_checked: number;
  num_images_censored: number;
  num_conversation_parts_checked: number;
  num_conversation_parts_censored: number;
};

export function getCSShieldStats(userId: string): Promise<CsShieldStatsType> {
  const token = getToken();
  return fetchData(SERVER_DOMAIN + `/api/get-cs-shield-stats/${userId}/`, {
    Authorization: `Bearer ${token}`,
  });
}

export function getAttributesList(profileId: string): Promise<string[]> {
  return fetchData(
    SERVER_DOMAIN + `/api/get-attributes-list/${profileId}/`,
    null,
  );
}

export function getAvailableAttributesList(
  profileId: string,
): Promise<IAvailableAttributesResponse> {
  return fetchData(
    SERVER_DOMAIN + `/api/get-available-attributes-list/${profileId}/`,
    null,
  );
}

export function postSaveAttributesList(
  attributesList: string[],
  profileId: string,
) {
  return postData(
    SERVER_DOMAIN + `/api/save-attributes-list/${profileId}/`,
    {
      attributesList,
    },
    null,
  );
}

function normalizeCustomField(field: Record<string, unknown>): ICustomField {
  return {
    gid: field.gid as string,
    name: field.name as string,
    type: field.type as string | undefined,
    enum_values: field.enum_values as any[] | undefined,
    existsInWorkspace: field.exists_in_workspace as boolean | undefined,
  };
}

export async function getCustomFieldList(profileId: string) {
  const fields = await fetchData(
    SERVER_DOMAIN + `/api/get-custom-fields/${profileId}/`,
    null,
  );
  return fields.map((field: Record<string, unknown>) =>
    normalizeCustomField(field),
  );
}

export function postSaveCustomFields(
  customFieldsList: ICustomField[],
  profileId: string,
) {
  return postData(
    SERVER_DOMAIN + `/api/save-custom-fields/${profileId}/`,
    {
      customFieldsList,
    },
    null,
  );
}

export async function postAddCustomField(
  profileId: string,
  field: Pick<ICustomField, "gid" | "name">,
) {
  const response = await postData(
    SERVER_DOMAIN + `/api/add-custom-field/${profileId}/`,
    { gid: field.gid, name: field.name },
    null,
  );
  if (!response.ok) {
    throw new Error("Failed to add custom field");
  }
  return normalizeCustomField(await response.json());
}

export async function postRemoveCustomField(profileId: string, gid: string) {
  const response = await postData(
    SERVER_DOMAIN + `/api/remove-custom-field/${profileId}/`,
    { gid },
    null,
  );
  if (!response.ok) {
    throw new Error("Failed to remove custom field");
  }
}

export async function getAsanaCustomFieldList(
  query: string,
  profileId: string,
) {
  const response = await fetch(
    SERVER_DOMAIN +
      `/api/get-asana-custom-fields/${profileId}?query=${encodeURIComponent(query)}`,
    { headers: {} },
  );
  if (!response.ok) {
    throw new Error("Failed to search custom fields");
  }
  const results = await response.json();
  if (!Array.isArray(results)) {
    return [];
  }
  return results.filter(
    (field: Record<string, unknown>) => field.gid && field.name,
  );
}

function normalizeMondayColumn(field: Record<string, unknown>): IMondayColumn {
  return {
    boardId: String(field.board_id),
    boardName: (field.board_name as string) || "",
    columnId: field.column_id as string,
    name: field.name as string,
    type: field.type as string | undefined,
    enumValues: field.enum_values as
      | { label: string; monday_id?: string | null }[]
      | undefined,
    existsOnBoard: field.exists_on_board as boolean | undefined,
  };
}

export async function getMondayBoardsForEditor(
  query: string,
  profileId: string,
): Promise<IMondayBoard[]> {
  const response = await fetch(
    SERVER_DOMAIN +
      `/api/get-monday-boards/${profileId}?query=${encodeURIComponent(query)}`,
    { headers: {} },
  );
  if (!response.ok) {
    throw new Error("Failed to search monday boards");
  }
  const results = await response.json();
  if (!Array.isArray(results)) {
    return [];
  }
  return results
    .filter((board: Record<string, unknown>) => board.board_id && board.name)
    .map((board: Record<string, unknown>) => ({
      boardId: String(board.board_id),
      name: board.name as string,
    }));
}

export async function getMondayBoardColumns(
  profileId: string,
  boardId: string,
): Promise<{ boardId: string; boardName: string; columns: IMondayColumn[] }> {
  const response = await fetch(
    SERVER_DOMAIN +
      `/api/get-monday-board-columns/${profileId}?board_id=${encodeURIComponent(boardId)}`,
    { headers: {} },
  );
  if (!response.ok) {
    throw new Error("Failed to load monday board columns");
  }
  const data = await response.json();
  const columns = Array.isArray(data.columns)
    ? data.columns.map((column: Record<string, unknown>) =>
        normalizeMondayColumn({
          ...column,
          board_id: data.board_id || boardId,
          board_name: data.board_name || "",
        }),
      )
    : [];
  return {
    boardId: String(data.board_id || boardId),
    boardName: (data.board_name as string) || "",
    columns,
  };
}

export async function getMondayColumnList(profileId: string) {
  const fields = await fetchData(
    SERVER_DOMAIN + `/api/get-monday-columns/${profileId}/`,
    null,
  );
  return fields.map((field: Record<string, unknown>) =>
    normalizeMondayColumn(field),
  );
}

export async function postAddMondayColumn(
  profileId: string,
  column: {
    boardId: string;
    boardName: string;
    columnId: string;
    name: string;
  },
) {
  const response = await postData(
    SERVER_DOMAIN + `/api/add-monday-column/${profileId}/`,
    {
      board_id: column.boardId,
      board_name: column.boardName,
      column_id: column.columnId,
      name: column.name,
    },
    null,
  );
  if (!response.ok) {
    throw new Error("Failed to add monday column");
  }
  return normalizeMondayColumn(await response.json());
}

export async function postRemoveMondayColumn(
  profileId: string,
  boardId: string,
  columnId: string,
) {
  const response = await postData(
    SERVER_DOMAIN + `/api/remove-monday-column/${profileId}/`,
    { board_id: boardId, column_id: columnId },
    null,
  );
  if (!response.ok) {
    throw new Error("Failed to remove monday column");
  }
}
